package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminProductLicenseStatus
import org.gymly.theme.AdminColors

@Composable
fun SettingsLicensePanel(
    canActivate: Boolean,
    compact: Boolean,
) {
    val scope = rememberCoroutineScope()
    var status by remember { mutableStateOf<AdminProductLicenseStatus?>(null) }
    var code by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var okMsg by remember { mutableStateOf<String?>(null) }

    fun reload() {
        scope.launch {
            busy = true
            error = null
            runCatching { AdminRepo.api.getProductLicenseStatus() }
                .onSuccess { status = it }
                .onFailure { error = it.message ?: "No se pudo cargar la licencia" }
            busy = false
        }
    }

    LaunchedEffect(Unit) { reload() }

    val s = status
    Column(
        Modifier.fillMaxWidth().padding(vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Text(
            if (canActivate) "Licencia / Activación" else "Estado de licencia",
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
            color = AdminColors.Text,
        )
        Text(
            if (canActivate) {
                "Activa o renueva la instalación con el código firmado por Gymly. La clave privada nunca vive en este servidor."
            } else {
                "Vista de solo lectura. Para renovar o cambiar canales, contacte a Gymly."
            },
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )

        if (s != null) {
            StatusCard(s)
            FeatureRow(s)
            Text("Install ID", fontWeight = FontWeight.SemiBold, fontSize = 12.sp, color = AdminColors.TextMuted)
            Text(s.installId, fontSize = 13.sp, color = AdminColors.Text)
        }

        if (error != null) {
            Text(error!!, color = AdminColors.Error, fontSize = 13.sp)
        }
        if (okMsg != null) {
            Text(okMsg!!, color = AdminColors.Primary, fontSize = 13.sp)
        }

        if (canActivate) {
            OutlinedTextField(
                value = code,
                onValueChange = { code = it },
                modifier = Modifier.fillMaxWidth().height(if (compact) 120.dp else 140.dp),
                label = { Text("Código de activación / renovación") },
                placeholder = { Text("GYMLY1.…") },
                enabled = !busy,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Button(
                    onClick = {
                        scope.launch {
                            busy = true
                            error = null
                            okMsg = null
                            runCatching { AdminRepo.api.activateProductLicense(code.trim()) }
                                .onSuccess {
                                    status = it
                                    code = ""
                                    okMsg = "Licencia aplicada: ${it.statusLabelEs}"
                                }
                                .onFailure { error = it.message ?: "Activación fallida" }
                            busy = false
                        }
                    },
                    enabled = !busy && code.isNotBlank(),
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary),
                ) {
                    Text(if (busy) "…" else "Activar / Renovar")
                }
                Button(
                    onClick = { reload() },
                    enabled = !busy,
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.SurfaceContainer),
                ) {
                    Text("Actualizar", color = AdminColors.Text)
                }
            }
        } else {
            Button(
                onClick = { reload() },
                enabled = !busy,
                colors = ButtonDefaults.buttonColors(containerColor = AdminColors.SurfaceContainer),
            ) {
                Text("Actualizar", color = AdminColors.Text)
            }
        }
    }
}

@Composable
private fun StatusCard(s: AdminProductLicenseStatus) {
    val bg = when (s.status.uppercase()) {
        "ACTIVE" -> AdminColors.Primary.copy(alpha = 0.12f)
        "GRACE" -> Color(0xFFFFF3E0)
        "EXPIRED", "NOT_ACTIVATED" -> AdminColors.Error.copy(alpha = 0.12f)
        else -> AdminColors.SurfaceContainer
    }
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(bg)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.4f), RoundedCornerShape(12.dp))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(s.statusLabelEs, fontWeight = FontWeight.Bold, color = AdminColors.Text, fontSize = 16.sp)
        if (s.indefinite) {
            Text("Uso indefinido (sin fecha de vencimiento)", fontSize = 13.sp, color = AdminColors.Text)
        } else if (s.expiresAt != null) {
            val rem = s.daysRemaining?.let { " · $it día(s) restantes" } ?: ""
            Text("Vence: ${s.expiresAt}$rem", fontSize = 13.sp, color = AdminColors.Text)
            if (s.status.equals("GRACE", true)) {
                Text(
                    "Prórroga: quedan ${s.graceDaysRemaining ?: 0} de ${s.graceDays} día(s)",
                    fontSize = 13.sp,
                    color = AdminColors.Text,
                )
            } else if (s.graceDays > 0) {
                Text("Prórroga contratada: ${s.graceDays} día(s)", fontSize = 12.sp, color = AdminColors.TextMuted)
            }
        }
        s.message?.takeIf { it.isNotBlank() }?.let {
            Text(it, fontSize = 12.sp, color = AdminColors.TextMuted)
        }
    }
}

@Composable
private fun FeatureRow(s: AdminProductLicenseStatus) {
    val items = listOf(
        "admin" to "Panel admin",
        "portal" to "Portal web",
        "mobile_android" to "App Android",
        "mobile_ios" to "App iPhone",
    )
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        items.forEach { (code, label) ->
            val on = s.features.any { it.equals(code, true) } &&
                (s.status.equals("ACTIVE", true) || s.status.equals("GRACE", true))
            Text(
                if (on) "✓ $label" else "— $label",
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(horizontal = 10.dp, vertical = 8.dp),
                fontSize = 12.sp,
                color = if (on) AdminColors.Text else AdminColors.TextMuted,
                fontWeight = FontWeight.SemiBold,
            )
            if (code != "mobile_ios") Spacer(Modifier.width(0.dp))
        }
    }
}
